import {
  Avatar,
  Box,
  Grid,
  HStack,
  Image,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Empty } from "../components/card/Empty";
import { ReadMore } from "../components/card/ReadMore";
import { CardProductMyStore } from "../components/dashboard_reseller/CardProductMyStore";
import SearchInput from "../components/input/SearchInput";
import { MenuSort } from "../components/menu/MenuSort";
import Pagination from "../components/Pagination";
import { CardSkeleton } from "../components/skeleton/CardSkeleton";
import { PaginationInterface } from "../constants/Pagination";
import {
  CardProductMyStoreInterface,
  ProductInterface,
} from "../constants/Product";
import { SelectOption } from "../constants/SelectOption";
import { StoreDetailInterface } from "../constants/Store";
import useCustomToast from "../lib/useCustomToast";
import useScreenWidth from "../lib/useScreenWidth";
import { useBorderColor } from "../themes/colors";
import { axiosPublic } from "../utils/AxiosInstance";
import ShareLinkModal from "../components/ShareLinkModal";
import { MetaTag } from "../utils/MetaTag";

const CategoryFilter = lazy(() => import("../components/menu/CategoryFilter"));
const FilterDrawer = lazy(() => import("../components/drawer/FilterDrawer"));

const ResellerProfile = () => {
  const { prefix } = useParams();
  const [data, setData] = useState<StoreDetailInterface | undefined>(undefined);
  const [dataProducts, setDataProducts] = useState<
    CardProductMyStoreInterface[] | undefined
  >(undefined);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationInterface | undefined>(
    undefined
  );
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>("");
  const [sort, setSort] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<SelectOption | undefined>(undefined);
  const sw = useScreenWidth();
  const toast = useCustomToast();
  const borderColor = useBorderColor();

  useEffect(() => {
    const categoryId = filter?.id ? filter?.id : "";

    axiosPublic
      .get(
        `${process.env.REACT_APP_API_URL}/v1/store/${prefix}?page=${page}&sort=${sort}&search=${search}&category=${categoryId}`
      )
      .then((res) => {
        setData(res.data.data);
        setPagination(res.data.data.paginator);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });

        if (err.response.status && err.response.status === 404) {
          window.location.href = `${process.env.REACT_APP_FRONTEND_URL}/404`;
        }
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [filter?.id, page, prefix, search, sort]);

  useEffect(() => {
    if (data) {
      const formattedData = data.products.map((item: ProductInterface) => ({
        id: item.id || "",
        name: item.name || "",
        slug: item.slug || "",
        image: item.main_picture || "",
        price: item.lowest_price || "",
        store_prefix: prefix || "",
      }));

      setDataProducts(formattedData);
    }
  }, [data, data?.products, prefix]);

  const goToDetailProduct = (id: any, prefix: string, slug: string) => {
    navigate(`/product/${prefix}/${slug}`);
  };

  return (
    <VStack
      className="root-store"
      w={"100%"}
      py={5}
      px={{
        base: "12px",
        sm: "24px",
        md: "32px",
        lg: "40px",
        xl: "48px",
        "2xl": 0,
      }}
    >
      <MetaTag
        title={data?.store.name || "Profil Toko"}
        description={
          data?.store.description ||
          "Toko yang menjual berbagai produk berkualitas"
        }
        image={
          data?.store.logo
            ? `${process.env.REACT_APP_MEDIA_URL}${data?.store.logo}`
            : undefined
        }
      />

      <VStack
        className="wrap-banner-avatar"
        w={"100%"}
        position={"relative"}
        h={["280px", null, "300px"]}
      >
        {data?.store.banner ? (
          <Image
            className="ratio-16-9"
            w={"100%"}
            h={"240px"}
            src={
              data.store.banner
                ? `${process.env.REACT_APP_MEDIA_URL}${data?.store.banner}`
                : ""
            }
            alt={data.store.name || "Profil Toko"}
            fallbackSrc={"/assets/images/placeholder_banner.png"}
            objectFit={"cover"}
            objectPosition={"center"}
            borderRadius={["lg", null, "xl"]}
          />
        ) : (
          <Box
            w={"100%"}
            h={"240px"}
            bgColor={borderColor}
            borderRadius={["lg", null, "xl"]}
          />
        )}

        <Avatar
          name={data?.store.name || ""}
          src={
            data?.store.logo
              ? `${process.env.REACT_APP_MEDIA_URL}${data?.store.logo}`
              : ""
          }
          size={["xl", null, "2xl"]}
          borderWidth={4}
          position={"absolute"}
          bottom={0}
        />
      </VStack>

      <VStack className="wrap-info" w={"100%"}>
        <SkeletonText isLoaded={loaded} noOfLines={!loaded ? 1 : undefined}>
          <Text textStyle={"h-title"}>{data?.store.name}</Text>
        </SkeletonText>

        <SkeletonText isLoaded={loaded} noOfLines={!loaded ? 3 : undefined}>
          <ReadMore data={data?.store.description} />
        </SkeletonText>

        {loaded && (
          <ShareLinkModal
            fontSize="10px"
            iconSize="16px"
            buttonText="Bagikan Link Toko"
            modalTitle="Bagikan Toko"
            modalDescription="Anda bisa membagikan toko ini melalui media sosial."
            mx={"auto"}
            data={`${process.env.REACT_APP_API_URL}/v1/sharelink/store/${data?.store.id}`}
          />
        )}
      </VStack>

      <HStack
        className="layout-list-product"
        w={"100%"}
        mt={4}
        alignItems={"start"}
      >
        {sw > 768 && (
          <Suspense fallback={<CardSkeleton row={1} height={435} />}>
            <CategoryFilter
              onSelected={(selectedFilter) => {
                setFilter(selectedFilter);
              }}
              onResetFilter={(reset) => {
                setFilter(undefined);
              }}
            />
          </Suspense>
        )}

        <VStack
          className="list-product"
          w={"100%"}
          px={sw > 768 ? 4 : 0}
          pb={sw > 768 ? 4 : 0}
          align={"stretch"}
        >
          <HStack w={"100%"} justify={"space-between"}>
            <SearchInput
              w={"fit-content"}
              placeholder="Cari produk toko ini..."
              onConfirm={(input) => {
                setSearch(input);
              }}
            />

            <HStack gap={[1, 2, 4]}>
              {sw < 768 && (
                <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                  <FilterDrawer
                    onSelected={(select) => {
                      setFilter(select);
                    }}
                    onResetFilter={(reset) => {
                      setFilter(undefined);
                    }}
                  />
                </Suspense>
              )}

              <MenuSort
                onSelected={(selected) => {
                  setSort(`${selected?.note}`);
                }}
                children={undefined}
              />
            </HStack>
          </HStack>

          {filter && (
            <HStack gap={1}>
              <Text fontSize={[10, null, 12]}>
                Menampilkan pencarian untuk filter
              </Text>
              <Text fontSize={[10, null, 12]} fontWeight={"semibold"}>
                {`${filter?.name}`}
              </Text>
            </HStack>
          )}

          {!loaded ? (
            <CardSkeleton row={3} column={2} height={300} mt={4} />
          ) : (
            <VStack w={"100%"}>
              {data?.products && data.products.length > 0 ? (
                <Grid
                  w={"100%"}
                  templateColumns={"repeat(auto-fill, minmax(160px, 1fr))"}
                  gap={4}
                  mt={4}
                >
                  {dataProducts &&
                    dataProducts.map((item) => (
                      <CardProductMyStore
                        key={item.id}
                        pb={2}
                        data={item}
                        onClickItem={(id, prefix, slug) =>
                          goToDetailProduct(id, prefix, slug)
                        }
                      />
                    ))}
                </Grid>
              ) : (
                <Empty />
              )}

              {loaded &&
                pagination &&
                dataProducts &&
                dataProducts?.length > 0 && (
                  <Pagination
                    mt={4}
                    totalItems={pagination.total}
                    pageSize={pagination.per_page}
                    onPageChange={(page) => {
                      setPage(page);
                    }}
                  />
                )}
            </VStack>
          )}
        </VStack>
      </HStack>
    </VStack>
  );
};

export default ResellerProfile;
